'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { PortableText } from '@portabletext/react';
import type { PortableTextBlock } from 'sanity';

import { cn } from '@/lib/utils';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { Accordion, AccordionContent, AccordionItem } from '@/shadcn/ui/accordion';
import { RevealBlock } from './about/reveal';

function ToggleIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="#161616"
      aria-hidden
      className="shrink-0 w-[2rem] h-[2rem]">
      <path
        className="group-data-[state=open]/trigger:hidden"
        d="M8.5 7.5V2.5H7.5V7.5H2.5V8.5H7.5V13.5H8.5V8.5H13.5V7.5H8.5Z"
      />
      <path
        className="hidden group-data-[state=open]/trigger:block"
        d="M2.5 7.5V8.5H13.5V7.5H2.5Z"
      />
    </svg>
  );
}

export default function FAQListNewWebsite({
  faq,
  className,
}: {
  faq: {
    question: string;
    answer: PortableTextBlock[];
  }[];
  className?: string;
}) {
  if (!faq?.length) return null;

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="item-1"
      className={cn('flex flex-col gap-[1.25rem] lg:gap-[2rem]', className)}>
      {faq.map((item, index) => (
        <RevealBlock
          key={index + 1}
          index={index}>
          <AccordionItem
            value={`item-${index + 1}`}
            className="flex flex-col rounded-[0.25rem] p-[3rem] max-lg:border max-lg:border-black/10 lg:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] data-[state=open]:border-0 data-[state=open]:shadow-none data-[state=open]:bg-gradient-to-b data-[state=open]:from-black/5 data-[state=open]:to-black/[3%]">
            <AccordionPrimitive.Header>
              <AccordionPrimitive.Trigger className="group/trigger flex w-full items-center gap-[3rem] text-left outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9FE0B0]">
                <span className="flex-1 font-geist text-[2.25rem] font-[400] leading-[1.15] text-black lg:leading-[1.12]">{item.question}</span>
                <ToggleIcon />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>

            <AccordionContent className="pb-0">
              {item?.answer && (
                <div className="richtext richtext-start [text-wrap:wrap] mt-[1.25rem] w-[min(41rem,100%)] lg:w-full prose-p:font-geist prose-p:text-black/50 prose-p:text-[2rem] prose-p:leading-[1.5] lg:prose-p:text-[2.25rem]">
                  <PortableText
                    value={item.answer}
                    components={richTextComponent}
                  />
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </RevealBlock>
      ))}
    </Accordion>
  );
}
